// @flow
import React, { useEffect, useState } from 'react';
import type { Node } from 'react';
import Debug from 'debug';
import 'ol/ol.css';
import OlMap from 'ol/Map';
import View from 'ol/View';
import type { MapType } from './types';
import { OpenLayersProvider } from './context';

const debug = Debug('amms:modules:openlayers:map');

const style = {
    height: '100%'
};

type Props = $ReadOnly<{|
    view: $ReadOnly<{
        center: [number, number],
        zoom: number,
        projection?: string
    }>,
    controls?: $ReadOnlyArray<>,
    mapRef?: (map: null | MapType) => void | {current: null | MapType},
    children: Node,

    onClick?: (e) => void
|}>;

function MapComponent({ view, controls, mapRef, children, onClick, onZoom }: Props) {
    const [map, setMap] = useState<?MapType>();
    const [target, setTarget] = useState();
    const [featureListeners] = useState({});

    useEffect(() => {
        debug('Creating map');

        setMap(new OlMap({
            controls
        }));
    }, [controls]);

    const getRef = (element) => {
        if(element && !target) {
            debug('Setting map target', element);
            setTarget(element);
        }
    };

    useEffect(() => {
        if(map) {
            debug('Setting view to ', view);
            map.setView(new View(view));
        }
    }, [map, view]);

    useEffect(() => {
        if(map) {
            const currentTarget = map.getTarget();

            if(target && target !== currentTarget) {
                debug('Mounting map');

                if(currentTarget) {
                    map.setTarget(null);
                }

                map.setTarget(target);

                if(debug.enabled) {
                    map.on('click', function(e){
                        console.info('Clicked at coordinates: ', e.coordinate);
                    });
                }
            }
        }

        return () => {
            if(map) {
                debug('Unmounting map');
                map.setTarget(null);
            }
        };
    }, [map, target]);

    useEffect(() => {
        if(mapRef) {
            if(typeof mapRef === 'function') {
                mapRef(map);
            }
            else {
                mapRef.current = map;
            }
        }
    }, [map, mapRef]);

    useEffect(() => {
        if(map && onClick) {
            debug('Adding map click event');
            map.on('click', onClick);
        }

        return () => {
            if(map && onClick) {
                debug('Removing map click event');
                map.un('click', onClick);
            }
        };
    }, [map, onClick]);

    useEffect(() => {
        let view;
        if(map && onZoom) {
            debug('Adding map zoom event');

            view = map.getView();

            if(view) {
                view.on('change:resolution', onZoom);
            }
        }

        return () => {
            if(map && onZoom && view) {
                debug('Removing map zoom event');
                view.un('change:resolution', onZoom);
            }
        };
    }, [map, view, onZoom]);

    return (
        <div ref={getRef} style={style}>
            <OpenLayersProvider map={map} featureListeners={featureListeners}>
                {children}
            </OpenLayersProvider>
        </div>
    );
}

export default MapComponent;